/**
 * Cosa ha fatto una scansione, letto dagli eventi del suo rapporto.
 *
 * ## Il difetto che questo file esiste per chiudere
 *
 * Il rapporto di una discovery mostrava un registro di eventi. Un registro
 * risponde bene a «cosa è andato storto» e male a **«cosa è successo»**: una
 * scansione riuscita produceva poche righe informative in mezzo agli avvisi, e
 * per sapere se il passo SSH era partito su diciotto host o su zero bisognava
 * leggerle tutte — sempre che ci fossero.
 *
 * La sonda ora scrive una riga per passo con i suoi numeri. Qui quelle righe
 * tornano a essere una tabella: il registro resta, ma sopra c'è la risposta.
 *
 * ## Perché la logica sta fuori dal componente
 *
 * Perché si possa provare. Un componente React si prova rendendolo, e ciò che
 * va verificato qui non è che compaia una tabella: è che un passo mai partito si
 * distingua da un passo partito e andato a vuoto, e che i numeri siano quelli
 * che la sonda ha mandato.
 */

/** La forma minima di un evento del rapporto: quella che questo file legge. */
import { t } from './i18n/lingua'

export type ReportEvent = {
  level: string
  stage: string
  message: string
  details?: Record<string, unknown> | null
}

/** Una riga della tabella dei passi. */
export type StepRow = {
  id: string
  probe: string
  targets: number
  executed: number
  ok: number
  failed: number
  skippedCondition: number
  skippedUnknown: number
  durationMs: number
  attributes: number
  interfaces: number
  relations: number
  enriched: number
  /**
   * Cosa è successo a questo passo, in una parola.
   *
   * Le tre situazioni portano a tre azioni diverse e vanno distinte:
   * - `ok`: è partito e ha raccolto;
   * - `vuoto`: è partito e non ha trovato niente — l'host ha risposto e non
   *   aveva quel dato, oppure il passo raccoglie qualcosa che lì non c'è;
   * - `mai`: non è partito su nessun indirizzo. Non è un errore, la scansione
   *   riesce, e la conclusione di chi guarda è che quegli host non hanno quel
   *   dato. È il difetto che i piani condizionati producono, ed è invisibile.
   */
  outcome: 'ok' | 'vuoto' | 'mai' | 'sconosciuto'
}

/** Il riepilogo di ciò che la scansione ha raccolto. */
export type Collected = {
  cis: number
  byType: Record<string, number>
  interfaces: number
  relations: number
  software: number
  inventories: number
}

function asNumber(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : 0
}

/**
 * Le righe dei passi, nell'ordine in cui la sonda le ha scritte.
 *
 * Nell'ordine di arrivo e non riordinate: è l'ordine del piano, ed è
 * l'informazione che serve a capire perché un passo non è partito — dipende da
 * quelli prima.
 */
export function planSteps(events: ReportEvent[]): StepRow[] {
  const rows: StepRow[] = []
  for (const event of events) {
    const details = event.details ?? {}
    const id = details.passo
    if (typeof id !== 'string' || !('eseguito_su' in details)) continue

    const executed = asNumber(details.eseguito_su)
    const attributes = asNumber(details.attributi_raccolti)
    const interfaces = asNumber(details.interfacce_raccolte)
    const relations = asNumber(details.relazioni_raccolte)
    const skippedUnknown = asNumber(details.saltati_sconosciuto)

    let outcome: StepRow['outcome'] = 'ok'
    if (executed === 0) {
      outcome = skippedUnknown > 0 ? 'sconosciuto' : 'mai'
    } else if (attributes === 0 && interfaces === 0 && relations === 0) {
      outcome = 'vuoto'
    }

    rows.push({
      id,
      probe: typeof details.tipo === 'string' ? details.tipo : '',
      targets: asNumber(details.bersagli),
      executed,
      ok: asNumber(details.riusciti),
      failed: asNumber(details.falliti),
      skippedCondition: asNumber(details.saltati_condizione),
      skippedUnknown,
      durationMs: asNumber(details.durata_ms),
      attributes,
      interfaces,
      relations,
      enriched: asNumber(details.bersagli_arricchiti),
      outcome,
    })
  }
  return rows
}

/**
 * Il riepilogo del raccolto, se la sonda l'ha mandato.
 *
 * `null` quando non c'è: una sonda più vecchia non lo produce, e mostrare zeri
 * al posto di «non lo so» direbbe che la scansione non ha raccolto niente.
 */
export function collected(events: ReportEvent[]): Collected | null {
  for (const event of events) {
    const details = event.details ?? {}
    if (!('ci_raccolti' in details)) continue
    const byType: Record<string, number> = {}
    const raw = details.per_tipo
    if (raw && typeof raw === 'object') {
      for (const [name, count] of Object.entries(raw as Record<string, unknown>)) {
        byType[name] = asNumber(count)
      }
    }
    return {
      cis: asNumber(details.ci_raccolti),
      byType,
      interfaces: asNumber(details.interfacce),
      relations: asNumber(details.relazioni),
      software: asNumber(details.prodotti_sw),
      inventories: asNumber(details.schede_macchina),
    }
  }
  return null
}

/**
 * Cosa è entrato in CMDB, secondo il Control Plane.
 *
 * È un numero **diverso** da quello della sonda, e la differenza è il punto:
 * «raccolti 40, salvati 12» dice che ventotto sono stati scartati, e il motivo è
 * negli eventi di fase `ingest`. Fonderli in un numero solo nasconderebbe
 * esattamente ciò che si vuole vedere.
 */
export type Ingested = {
  received: number
  accepted: number
  created: number
  updated: number
  byClass: Record<string, number>
}

export function ingested(events: ReportEvent[]): Ingested | null {
  for (const event of events) {
    const details = event.details ?? {}
    if (!('ci_nuovi' in details)) continue
    const byClass: Record<string, number> = {}
    const raw = details.per_classe
    if (raw && typeof raw === 'object') {
      for (const [name, count] of Object.entries(raw as Record<string, unknown>)) {
        byClass[name] = asNumber(count)
      }
    }
    return {
      received: asNumber(details.ci_ricevuti),
      accepted: asNumber(details.ci_accettati),
      created: asNumber(details.ci_nuovi),
      updated: asNumber(details.ci_aggiornati),
      byClass,
    }
  }
  return null
}

/** «2,3 s», «412 ms»: i millisecondi grezzi non si leggono. */
export function duration(ms: number): string {
  if (ms <= 0) return '—'
  if (ms < 1000) return `${ms} ms`
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)} s`
  const minutes = Math.floor(ms / 60_000)
  const seconds = Math.round((ms % 60_000) / 1000)
  return `${minutes} min ${seconds} s`
}

/** «12 switch, 18 host», dal più numeroso. */
export function describeTypes(counts: Record<string, number>): string {
  const entries = Object.entries(counts).sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
  if (entries.length === 0) return t('nessun oggetto')
  return entries.map(([name, count]) => `${count} ${name}`).join(', ')
}
